/**
 * One file, sent as a request's whole body — the transport under every upload the SPA
 * makes: a request's attachment (`api/client/requests.ts` `useUploadAttachment`) and a
 * blog image (`api/blogUpload.ts`).
 *
 * **Why not `fetch`.** A client sends footage here — up to 2 GB a file — and `fetch`
 * cannot say how much of a body has gone: a streamed request body would, but only
 * Chromium sends one, and only over HTTP/2. `XMLHttpRequest`'s `upload.onprogress` is
 * the one API every browser has that reports bytes sent, so it is the browser's
 * uploader. Everything else about it is made to look like `fetch` — it answers a
 * `Response`, rejects with a `TypeError` when no answer came and with an `AbortError`
 * when it was called off — so the two callers read a refusal through `problemOf` and a
 * silence as a `NetworkError` exactly as they did.
 *
 * **A test's fake fetch is the whole network**, uploads included: `api/client.ts` hands
 * `fetchUpload` the fake, so `fakeApi` answers a file the way it answers everything
 * else, and its progress is the one report at the end.
 *
 * **The two headers are this file's, once.** The media type is the file's own (the
 * server sniffs the bytes and believes neither), and the name is percent-encoded,
 * because a header is Latin-1 and "Küçüktaş.jpg" is not — `fetch` and
 * `XMLHttpRequest` both throw before a byte leaves. The API decodes it
 * (`apps/api/src/http/fileName.ts`).
 */

/** The header an upload carries its file name in — the API's `FILE_NAME_HEADER`. */
export const FILE_NAME_HEADER = 'X-File-Name'

/** Hears the bytes as they go: how many have been sent, of how many. */
export type UploadProgress = (sent: number, total: number) => void

export type UploadRequest = {
  /** Where the file goes — a path on this origin. */
  readonly path: string
  readonly file: File
  readonly onProgress?: UploadProgress | undefined
  /** Calls the upload off; the promise rejects with an `AbortError`. */
  readonly signal?: AbortSignal | undefined
}

/** Sends one file, and answers with the response — or rejects as `fetch` would. */
export type Uploader = (request: UploadRequest) => Promise<Response>

/** What every file upload says about the file beside its bytes. */
export function fileHeaders(file: File): Record<string, string> {
  return {
    'Content-Type': file.type === '' ? 'application/octet-stream' : file.type,
    [FILE_NAME_HEADER]: encodeURIComponent(file.name),
  }
}

/** The browser's uploader: the one that can say how far a file has got. */
export const xhrUpload: Uploader = ({ path, file, onProgress, signal }) =>
  new Promise<Response>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(aborted())
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open('POST', path)
    // Same origin, so the session cookie goes anyway; this is `fetch`'s
    // `credentials: 'include'`, said for the day the SPA is on a second hostname.
    xhr.withCredentials = true
    for (const [name, value] of Object.entries(fileHeaders(file))) {
      xhr.setRequestHeader(name, value)
    }

    if (onProgress !== undefined) {
      xhr.upload.onprogress = (event) => {
        onProgress(event.loaded, event.lengthComputable ? event.total : file.size)
      }
    }

    /*
     * The signal outlives the upload — the composer hands every file the one its account
     * holds — so the listener is taken off again however the upload ends, rather than
     * left on it for each file sent.
     */
    const callOff = () => {
      xhr.abort()
    }
    signal?.addEventListener('abort', callOff, { once: true })
    const settle = (finish: () => void) => {
      signal?.removeEventListener('abort', callOff)
      finish()
    }

    xhr.onload = () => {
      settle(() => {
        // Status 0 is no answer at all, which `fetch` would have rejected.
        if (xhr.status === 0) {
          reject(new TypeError('The upload got no answer.'))
          return
        }
        // Never left pending: a promise nothing settles would hold the composer's
        // queue for good.
        try {
          resolve(responseOf(xhr))
        } catch (error) {
          reject(
            error instanceof Error ? error : new TypeError('The upload’s answer was unreadable.'),
          )
        }
      })
    }
    xhr.onerror = () => {
      settle(() => {
        reject(new TypeError('The upload got no answer.'))
      })
    }
    xhr.ontimeout = xhr.onerror
    xhr.onabort = () => {
      settle(() => {
        reject(aborted())
      })
    }

    xhr.send(file)
  })

/** An uploader over a `fetch` — a test's fake. Progress is the one report at the end. */
export function fetchUpload(fetchImpl: typeof fetch): Uploader {
  return async ({ path, file, onProgress, signal }) => {
    const response = await fetchImpl(path, {
      method: 'POST',
      credentials: 'include',
      headers: fileHeaders(file),
      body: file,
      ...(signal === undefined ? {} : { signal }),
    })
    onProgress?.(file.size, file.size)
    return response
  }
}

/** What `fetch` rejects with when it is called off. */
function aborted(): DOMException {
  return new DOMException('The upload was called off.', 'AbortError')
}

/**
 * The finished request as a `Response`, so a caller reads it as it reads any other.
 *
 * The headers are parsed back out of `getAllResponseHeaders()` because `problemOf`
 * reads the content type to decide whether a refusal is the API's or the proxy's. A
 * status that may not carry a body is given none, which the constructor insists on.
 */
function responseOf(xhr: XMLHttpRequest): Response {
  const headers = new Headers()
  for (const line of xhr.getAllResponseHeaders().split('\r\n')) {
    const colon = line.indexOf(':')
    if (colon <= 0) continue
    headers.append(line.slice(0, colon).trim(), line.slice(colon + 1).trim())
  }
  const bodiless = xhr.status === 204 || xhr.status === 205 || xhr.status === 304
  return new Response(bodiless ? null : xhr.responseText, {
    status: xhr.status,
    statusText: xhr.statusText,
    headers,
  })
}
